import {
  AppointmentDto,
  DiscussionDto,
  DiscussionId,
  EtxeApiPrefixes,
  MessageDto,
  PropertyApplicationDto,
} from '@workspace/data-types';
import { Observable } from 'rxjs';
import { ApiCaller } from '../../../../../api-caller.service';
import { ApiConnector } from '../../../../../api-connector.abstract-service';

export class DiscussionsAgentConnector extends ApiConnector<DiscussionDto, DiscussionDto, DiscussionId> {
  constructor(apiCaller: ApiCaller, apiPrefix: string, parentSlug: string) {
    super(apiCaller, apiPrefix, `${parentSlug}/agent`);
  }

  getDiscussions = (archived?: boolean, limit?: number, skip?: number): Observable<DiscussionDto[]> => {
    const params: Record<string, string> = {};
    if (archived !== undefined) params['archived'] = String(archived);
    if (limit !== undefined) params['limit'] = String(limit);
    if (skip !== undefined) params['skip'] = String(skip);
    return this.apiCaller.getRequest<DiscussionDto[]>(
      EtxeApiPrefixes.CORE,
      `${this.slug}`,
      Object.keys(params).length > 0 ? { params } : undefined,
    );
  };

  getDiscussionMessages = (discussionId: string, limit?: number, skip?: number): Observable<MessageDto[]> => {
    const params: Record<string, string> = {};
    if (limit !== undefined) params['limit'] = String(limit);
    if (skip !== undefined) params['skip'] = String(skip);
    return this.apiCaller.getRequest<MessageDto[]>(
      EtxeApiPrefixes.CORE,
      `${this.slug}/${discussionId}/messages`,
      Object.keys(params).length > 0 ? { params } : undefined,
    );
  };

  sendMessage = (discussionId: string, text: string, replyTo?: string): Observable<MessageDto> =>
    this.apiCaller.postRequest<MessageDto>(EtxeApiPrefixes.CORE, `${this.slug}/${discussionId}/messages`, {
      text,
      ...(replyTo && { replyTo }),
    });

  markAsUnread = (discussionId: string): Observable<{ success: boolean }> =>
    this.apiCaller.postRequest<{ success: boolean }>(
      EtxeApiPrefixes.CORE,
      `${this.slug}/${discussionId}/mark-unread`,
      {},
    );

  setStarred = (discussionId: string, isStarred: boolean): Observable<{ success: boolean }> =>
    this.apiCaller.postRequest<{ success: boolean }>(
      EtxeApiPrefixes.CORE,
      `${this.slug}/${discussionId}/star`,
      {
        isStarred,
      },
    );

  setArchived = (discussionId: string, isArchived: boolean): Observable<{ success: boolean }> =>
    this.apiCaller.postRequest<{ success: boolean }>(
      EtxeApiPrefixes.CORE,
      `${this.slug}/${discussionId}/archive`,
      {
        isArchived,
      },
    );

  pinMessage = (discussionId: string, messageId: string | null): Observable<{ success: boolean }> =>
    this.apiCaller.postRequest<{ success: boolean }>(
      EtxeApiPrefixes.CORE,
      `${this.slug}/${discussionId}/pin`,
      { messageId },
    );

  deleteMessage = (discussionId: string, messageId: string): Observable<{ success: boolean }> =>
    this.apiCaller.deleteRequest<{ success: boolean }>(
      EtxeApiPrefixes.CORE,
      `${this.slug}/${discussionId}/messages/${messageId}`,
    );

  getAppointment = (discussionId: string): Observable<AppointmentDto | null> =>
    this.apiCaller.getRequest<AppointmentDto | null>(
      EtxeApiPrefixes.CORE,
      `${this.slug}/${discussionId}/appointment`,
    );

  getApplication = (discussionId: string): Observable<PropertyApplicationDto | null> =>
    this.apiCaller.getRequest<PropertyApplicationDto | null>(
      EtxeApiPrefixes.CORE,
      `${this.slug}/${discussionId}/application`,
    );

  confirmAppointment = (discussionId: string, slotId: string, notes?: string): Observable<AppointmentDto> =>
    this.apiCaller.postRequest<AppointmentDto>(
      EtxeApiPrefixes.CORE,
      `${this.slug}/${discussionId}/appointment/confirm`,
      { slotId, notes },
    );

  requestRescheduleAppointment = (discussionId: string, reason?: string): Observable<AppointmentDto> =>
    this.apiCaller.postRequest<AppointmentDto>(
      EtxeApiPrefixes.CORE,
      `${this.slug}/${discussionId}/appointment/request-reschedule`,
      { reason },
    );

  completeAppointment = (discussionId: string): Observable<AppointmentDto> =>
    this.apiCaller.postRequest<AppointmentDto>(
      EtxeApiPrefixes.CORE,
      `${this.slug}/${discussionId}/appointment/complete`,
      {},
    );
}
